import {
  SelectionItem,
  useSelectionGroup,
} from '@/ux-common/hooks/use-selection-group/use-selection-group';

import { TabGroupContent } from './tab-group-content';
import { TabGroupProps, TabRenderProps } from './types';

/**
 * TabGroup — headless selection manager for a row of Tab triggers.
 *
 * TabGroup owns state and layout; the consumer owns rendering. Pass `items`
 * (the set of tabs) and a render function that returns a Tab element for
 * each. Supports controlled (`selectedIds`) and uncontrolled
 * (`initialSelectedIds`) modes, single- and multi-select, and scrollable
 * overflow.
 *
 * @example
 * ```tsx
 * const items = [
 *   { id: 'overview', data: { label: 'Overview' } },
 *   { id: 'activity', data: { label: 'Activity' } },
 * ];
 *
 * <TabGroup
 *   items={items}
 *   initialSelectedIds={['overview']}
 *   onSelectionChange={(selected) => console.log(selected)}
 * >
 *   {({ tabs }) =>
 *     tabs.map(({ tab, state, onToggle, disabled }) => (
 *       <Tab
 *         key={tab.id}
 *         data-state={state}
 *         onClick={onToggle}
 *         disabled={disabled}
 *       >
 *         {tab.data.label}
 *       </Tab>
 *     ))
 *   }
 * </TabGroup>
 * ```
 */
export function TabGroup<T>({
  items,
  children,
  initialSelectedIds,
  selectedIds,
  multiselect = false,
  onSelectionChange,
  className,
  disabled = false,
  scrollable = false,
  withScrollShadow = false,
}: TabGroupProps<T>) {
  const { isSelected, toggleSelection } = useSelectionGroup({
    items,
    initialSelectedIds,
    selectedIds,
    multiselect,
    onSelectionChange,
    disabled,
  });

  const tabs: TabRenderProps<T>[] = items.map((tab: SelectionItem<T>) => ({
    tab,
    state: isSelected(tab.id) ? 'active' : 'inactive',
    onToggle: () => toggleSelection(tab),
    disabled: disabled || tab.disabled || false,
  }));

  return (
    <TabGroupContent
      scrollable={scrollable}
      className={className}
      withScrollShadow={withScrollShadow}
    >
      {children({ tabs })}
    </TabGroupContent>
  );
}
